import { connect } from "react-redux";
import { buttonElementModel } from "../../../models/common/buttonElement.model";
import ButtonElementComponent from "../../common/ButtonElementComponent";
import ChargePointChargingPriceElementComponent from "../ChargePointChargingPriceElementComponent";
import { bindActionCreators } from "redux";
import { chargePointActions } from "../../../store/actions/chargePoint.actions";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import alertService from "../../../services/alert/alert.service";
import validationHelper from "../../../utils/helpers/validation.helper";
import { useState } from "react";
import chargingTransactionsErrorEnum from "../../../enums/errors/transactions/chargingTransactions/chargingTransactionsError.enum";
import queryParamsHelper from "../../../utils/helpers/queryParams.helper";
import paymentFailureReasonEnum from "../../../enums/payments/paymentFailureReason.enum";
import { transactionPaymentActions } from "../../../store/actions/transactions/transactionPayment.actions";
import { connectorStatusEnum } from "../../../enums/chargePoint/connectorStatusEnum";

const setpIds = { 
  chagePointDetails: 0, 
  personalDetails: 1, 
  validatePersonalDetails: 2, 
  paymentDetails: 3, 
  startChargingTransaction: 4 
};

export const StartChargingTransactionStepComponent = ({ chargePointActions, transactionPaymentActions, chargePoint }) => {
  const navigate = useNavigate();
  const { companyId, chargePointId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const clientId = queryParamsHelper.get(searchParams, 'clientId');
  const paymentTokenId = queryParamsHelper.get(searchParams, 'paymentTokenId');
  
  const [isLoading, setIsLoading] = useState(false);
  const isPreparing = chargePoint?.connectorStatus === connectorStatusEnum.Preparing;

  const handleStartTransactionClicked = async () => {
    if (!isPreparing) return;

    // Validate clientId exists
    if (!clientId || clientId === 'null' || clientId === 'undefined') {
      alertService.error("שגיאה", "פרטי לקוח לא נמצאו. אנא מלא את הפרטים האישיים ונסה שנית.");
      const newParams = queryParamsHelper.set(searchParams, 'step', setpIds.personalDetails.toString());
      setSearchParams(newParams, { replace: true });
      return;
    }

    // Validate paymentTokenId exists
    if (!paymentTokenId || paymentTokenId === 'null' || paymentTokenId === 'undefined') {
      alertService.error("שגיאה", "אמצעי תשלום לא נבחר. אנא בחר אמצעי תשלום ונסה שנית.");
      // Navigate back to payment selection step
      const newParams = queryParamsHelper.set(searchParams, 'step', setpIds.paymentDetails.toString());
      setSearchParams(newParams, { replace: true });
      return;
    }

    setIsLoading(true);
    const startChargingResponse = await chargePointActions.startChargingTransaction(clientId, chargePointId, paymentTokenId);
    if (!validationHelper.validateHttpResponse(startChargingResponse) || !startChargingResponse.payload?.transactionId) {
      const errorMessage = getErrorMessageByCode(startChargingResponse?.errorCode);
      alertService.error(errorMessage[0], errorMessage[1]);
      setIsLoading(false);

      // The clearing provider told us this stored card will never work again, so remove it rather than
      // leave the driver to pick it and fail a second time. Only this one classification is destructive -
      // a decline or a transient failure keeps the card.
      if (startChargingResponse?.paymentFailureReason === paymentFailureReasonEnum.PaymentMethodUnusable) {
        await transactionPaymentActions.deleteClientPaymentToken(paymentTokenId);
      }

      // "Please replace your payment method" has to lead somewhere: send the driver to the payment step.
      if (startChargingResponse?.errorCode === chargingTransactionsErrorEnum.FailedChargeConceptLimit) {
        const newParams = queryParamsHelper.setMultiple(searchParams, {
          paymentTokenId: null,
          step: setpIds.paymentDetails.toString(),
        });
        setSearchParams(newParams, { replace: true });
      }

      return;
    }

    if (startChargingResponse.payload.token) {
      // Navigate to transaction details page (GUID format)
      navigate(`/company/${companyId}/chargePoint/${chargePointId}/chargingTransaction/${startChargingResponse.payload?.token}`);
    } else {
      alertService.info("טעינה החלה בהצלחה אך לא ניתן לנווט לעמוד פרטי הטעינה", "אנא סרוק שנית הברקוד או הזן את מזהה המטען והמחבר בעמוד החיפוש");
      navigate("/");
    }
    setIsLoading(false);
  };

  const getErrorMessageByCode = (errorCode) => {
    let errorMessage = ["שגיאה בהתחלת טעינה", "וודא כי המחבר מחובר לרכב ונסה שנית."];

    switch (errorCode) {
      case chargingTransactionsErrorEnum.ConnectorStatusNotAllowed:
        errorMessage = ["שגיאה", "סטטוס המחבר אינו מאפשר התחלת טעינה. אנא וודא כי המחבר מחובר לרכב ונסה שוב מאוחר יותר."];
        break;
      case chargingTransactionsErrorEnum.FailedChargeConceptLimit:
        errorMessage = ["שגיאה", "שגיאה בחיוב תפיסת המסגרת. אנא החלף אמצעי תשלום ונסה שנית"];
        break;
      case chargingTransactionsErrorEnum.FailedStartTransactionOnRemote:
        errorMessage = ["שגיאה", "נכשל בשליחת בקשת תחילת טעינה בשרת המטענים. אנא נסה שוב מאוחר יותר."];
        break;
      case chargingTransactionsErrorEnum.FailedConnectWithRemoteServer:
        errorMessage = ["שגיאה", "לא ניתן להתחבר לשרת המרוחק. אנא נסה שוב מאוחר יותר."];
        break;
      default:
        errorMessage = ["שגיאה בהתחלת טעינה", "וודא כי המחבר מחובר לרכב ונסה שנית."];
        break;
    }

    return errorMessage;
  };

  const getSubmitButtonElement = () => {
    const buttonElementProps = {
      ...buttonElementModel,
      label: "התחל טעינה",
      onClick: handleStartTransactionClicked,
      loading: isLoading,
      className: "marginTop10",
      disabled: isLoading || !isPreparing,
    };
    return <ButtonElementComponent buttonElementProps={buttonElementProps} />;
  };

  return (
    <div className="flexColumn gap10">
      <ChargePointChargingPriceElementComponent chargePoint={chargePoint} />
      {getSubmitButtonElement()}
      {!isPreparing && (
        <div className="textAlignStart">
          שים לב! לתחילת טעינה יש לחבר את המחבר לרכב
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  transactionClientReducer: state.transactionClientReducer,
});

const mapDispatchToProps = (dispatch) => {
  return {
    chargePointActions: bindActionCreators(chargePointActions, dispatch),
    transactionPaymentActions: bindActionCreators(transactionPaymentActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(StartChargingTransactionStepComponent);
